import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Radio,
  message,
  Space,
  Popconfirm,
  Tag,
  Descriptions,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  ReloadOutlined,
  EyeOutlined,
  PrinterOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import { openPdfBase64AndPrint } from '../../../utils/printTicketPdf';
import MainLayout from '../../layout/layout';
import { usePermissions } from '../../../context/permissionsContext';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

// Función helper para parsear timestamps de PostgreSQL correctamente
// El servidor (Render) graba y devuelve hora local Argentina - interpretar como tal sin convertir
const parseTimestamp = (timestamp) => {
  if (!timestamp) return null;
  
  if (typeof timestamp === 'string') {
    // Sin zona horaria: el servidor envía hora local (Argentina) - usar tal cual
    if (!timestamp.includes('Z') && !timestamp.includes('+') && !timestamp.match(/[+-]\d{2}:\d{2}$/)) {
      return dayjs.tz(timestamp.replace(' ', 'T'), 'America/Argentina/Buenos_Aires');
    }
    return dayjs.utc(timestamp).tz('America/Argentina/Buenos_Aires');
  }
  
  if (timestamp instanceof Date) {
    return dayjs.utc(timestamp).tz('America/Argentina/Buenos_Aires');
  }
  
  return dayjs.utc(timestamp).tz('America/Argentina/Buenos_Aires');
};

const { TextArea } = Input;
const { RangePicker } = DatePicker;

const Ventas = () => {
  const { hasPermission } = usePermissions();
  const [ventas, setVentas] = useState([]);
  const [articulos, setArticulos] = useState([]);
  const [formasPago, setFormasPago] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalDetalleVisible, setModalDetalleVisible] = useState(false);
  const [ventaSeleccionada, setVentaSeleccionada] = useState(null);
  const [form] = Form.useForm();
  const metodoPago = Form.useWatch('metodo_pago', form);
  const fechaForm = Form.useWatch('fecha', form);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [detallesTemporal, setDetallesTemporal] = useState([]);
  const [articuloSeleccionado, setArticuloSeleccionado] = useState(null);
  const [cantidadTemporal, setCantidadTemporal] = useState(1);
  const [codigoBarrasInput, setCodigoBarrasInput] = useState('');
  const articuloSelectRef = useRef(null);
  const codigoBarrasRef = useRef(null);
  const selectArticuloAbiertoRef = useRef(false);
  const [tipoDestino, setTipoDestino] = useState('mostrador');
  const [canchaClubId, setCanchaClubId] = useState(null);
  const [mesaId, setMesaId] = useState(null);
  const [mesas, setMesas] = useState([]);
  const [mesasLibres, setMesasLibres] = useState([]);
  const [responsableMesa, setResponsableMesa] = useState('');
  const [canchas, setCanchas] = useState([]);
  const [turnosClub, setTurnosClub] = useState([]);
  const [turnoClubId, setTurnoClubId] = useState(null);
  const [jugadores, setJugadores] = useState([]);
  const [jugadorSeleccionado, setJugadorSeleccionado] = useState(null);
  const [fechaRango, setFechaRango] = useState([dayjs(), dayjs()]);
  const [porcentajeDescuento, setPorcentajeDescuento] = useState(0);
  const [saving, setSaving] = useState(false);
  const [reprintingId, setReprintingId] = useState(null);

  const loadVentas = async (rangoOverride = null) => {
    setLoading(true);
    try {
      const rango = rangoOverride != null ? rangoOverride : fechaRango;
      const params = new URLSearchParams();
      if (rango && rango[0]) {
        params.append('fecha_desde', dayjs(rango[0]).format('YYYY-MM-DD'));
      }
      if (rango && rango[1]) {
        params.append('fecha_hasta', dayjs(rango[1]).format('YYYY-MM-DD'));
      }
      
      const response = await apiClient.get(`/gestion/ventas?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setVentas(data);
      } else {
        message.error('Error al cargar las ventas');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar las ventas');
    } finally {
      setLoading(false);
    }
  };

  const loadArticulos = async () => {
    try {
      const response = await apiClient.get('/gestion/ventas/recursos/articulos');
      if (response.ok) {
        const data = await response.json();
        setArticulos(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error al cargar artículos:', error);
    }
  };

  const loadFormasPago = async () => {
    try {
      const response = await apiClient.get('/gestion/ventas/recursos/formas-pago');
      if (response.ok) {
        const data = await response.json();
        setFormasPago(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error al cargar formas de pago:', error);
    }
  };

  const loadMesas = async () => {
    try {
      const response = await apiClient.get('/gestion/mesas');
      if (response.ok) {
        const data = await response.json();
        setMesas((data || []).filter(m => m.mes_activo));
      }
    } catch (error) {
      console.error('Error al cargar mesas:', error);
    }
  };

  const loadMesasLibres = async (fecha) => {
    try {
      const fechaStr = fecha ? dayjs(fecha).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
      const response = await apiClient.get(`/gestion/ventas/recursos/mesas-libres?fecha=${fechaStr}`);
      if (response.ok) {
        const data = await response.json();
        setMesasLibres(data || []);
      } else {
        setMesasLibres([]);
      }
    } catch (error) {
      console.error('Error al cargar mesas libres:', error);
      setMesasLibres([]);
    }
  };

  const loadCanchas = async () => {
    try {
      const response = await apiClient.get('/club/canchas/activas');
      if (response.ok) {
        const data = await response.json();
        setCanchas(data || []);
      }
    } catch (error) {
      console.error('Error al cargar canchas:', error);
    }
  };

  /** Carga los turnos reservados del día (con reserva = tienen cliente) para el selector de venta por turno */
  const loadTurnosReservadosDelDia = async (fecha) => {
    try {
      const fechaStr = fecha ? dayjs(fecha).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
      const canchasRes = await apiClient.get('/club/canchas/activas');
      if (!canchasRes.ok) {
        setTurnosClub([]);
        return;
      }
      const canchasList = await canchasRes.json();
      const respuestas = await Promise.all(
        canchasList.map((c) => apiClient.get(`/club/turnos/cancha/${c.ccl_id}/fecha/${fechaStr}`))
      );
      const todosTurnos = [];
      for (const resp of respuestas) {
        if (resp.ok) {
          const data = await resp.json();
          todosTurnos.push(...(Array.isArray(data) ? data : []));
        }
      }
      const conReserva = todosTurnos.filter(
        (t) =>
          t.rcl_nombre_cliente &&
          Number.isInteger(Number(t.tcl_id)) &&
          !String(t.tcl_id).startsWith('clase')
      );
      setTurnosClub(conReserva);
    } catch (err) {
      console.error('Error al cargar turnos reservados del día:', err);
      setTurnosClub([]);
    }
  };

  const loadJugadores = async () => {
    try {
      const response = await apiClient.get('/club/jugadores');
      if (response.ok) {
        const data = await response.json();
        setJugadores(data || []);
      }
    } catch (error) {
      console.error('Error al cargar jugadores:', error);
    }
  };

  useEffect(() => {
    loadVentas();
    loadArticulos();
    loadFormasPago();
    loadMesas();
    loadCanchas();
    loadJugadores();
  }, []);

  // Cargar ventas cuando cambia el filtro de fechas
  useEffect(() => {
    if (fechaRango && fechaRango[0] && fechaRango[1]) {
      loadVentas();
    }
  }, [fechaRango]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (modalVisible && tipoDestino === 'mesa' && fechaForm) {
      loadMesasLibres(fechaForm);
    }
  }, [modalVisible, tipoDestino, fechaForm]);

  const handleOpenModal = () => {
    form.resetFields();
    form.setFieldsValue({
      fecha: dayjs(),
      metodo_pago: undefined,
      observaciones: undefined,
    });
    setDetallesTemporal([]);
    setArticuloSeleccionado(null);
    setCantidadTemporal(1);
    setCodigoBarrasInput('');
    setTipoDestino('mostrador');
    setCanchaClubId(null);
    setTurnoClubId(null);
    setTurnosClub([]);
    setMesaId(null);
    setMesasLibres([]);
    setResponsableMesa('');
    setJugadorSeleccionado(null);
    setPorcentajeDescuento(0);
    setModalVisible(true);
    setTimeout(() => codigoBarrasRef.current?.focus(), 100);
  };

  const enfocarEscanner = () => {
    if (selectArticuloAbiertoRef.current) return;
    setTimeout(() => codigoBarrasRef.current?.focus(), 0);
  };

  const enfocarSelectorArticulo = () => {
    setTimeout(() => {
      articuloSelectRef.current?.focus?.();
    }, 0);
  };

  const agregarLineaDetalle = (articulo, cantidad) => {
    const qty = Number(cantidad) || 1;
    if (!articulo) return false;

    if (articulo.art_mueve_stock && articulo.art_stock_actual < qty) {
      message.error(`Stock insuficiente. Disponible: ${articulo.art_stock_actual}`);
      return false;
    }

    const precioUnitario = Number(articulo.art_precio_venta) || 0;
    const nuevoDetalle = {
      articulo_id: articulo.art_id,
      articulo_nombre: articulo.art_nombre,
      cantidad: qty,
      precio_unitario: precioUnitario,
      subtotal: qty * precioUnitario,
    };

    setDetallesTemporal((prev) => [...prev, nuevoDetalle]);
    return true;
  };

  const handleAgregarDetalle = () => {
    if (!articuloSeleccionado) {
      message.warning('Seleccione un artículo');
      return;
    }

    const articulo = articulos.find((a) => a.art_id === articuloSeleccionado);
    if (!articulo) {
      message.error('Artículo no encontrado');
      return;
    }

    const indiceExistente = detallesTemporal.findIndex((d) => d.articulo_id === articuloSeleccionado);
    if (indiceExistente >= 0) {
      const nuevaCantidad = detallesTemporal[indiceExistente].cantidad + cantidadTemporal;
      if (articulo.art_mueve_stock && articulo.art_stock_actual < nuevaCantidad) {
        message.error(`Stock insuficiente para ${articulo.art_nombre}. Disponible: ${articulo.art_stock_actual}`);
        return;
      }
      handleActualizarCantidadDetalle(indiceExistente, nuevaCantidad);
      setArticuloSeleccionado(null);
      setCantidadTemporal(1);
      enfocarSelectorArticulo();
      return;
    }

    if (!agregarLineaDetalle(articulo, cantidadTemporal)) return;

    setArticuloSeleccionado(null);
    setCantidadTemporal(1);
    enfocarSelectorArticulo();
  };

  const handleEscanearCodigoBarras = (codigoRaw) => {
    const codigo = String(codigoRaw || '').trim();
    if (!codigo) return;

    const articulo = articulos.find(
      (a) => a.art_codigo_barras && String(a.art_codigo_barras).trim() === codigo
    );

    if (!articulo) {
      message.warning(`No se encontró artículo con código: ${codigo}`);
      setCodigoBarrasInput('');
      enfocarEscanner();
      return;
    }

    const indiceExistente = detallesTemporal.findIndex((d) => d.articulo_id === articulo.art_id);

    if (indiceExistente >= 0) {
      const nuevaCantidad = detallesTemporal[indiceExistente].cantidad + 1;
      if (articulo.art_mueve_stock && articulo.art_stock_actual < nuevaCantidad) {
        message.error(`Stock insuficiente para ${articulo.art_nombre}. Disponible: ${articulo.art_stock_actual}`);
        setCodigoBarrasInput('');
        enfocarEscanner();
        return;
      }
      handleActualizarCantidadDetalle(indiceExistente, nuevaCantidad);
    } else if (!agregarLineaDetalle(articulo, 1)) {
      setCodigoBarrasInput('');
      enfocarEscanner();
      return;
    }

    setCodigoBarrasInput('');
    enfocarEscanner();
  };

  const handleCodigoBarrasKeyDown = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    handleEscanearCodigoBarras(codigoBarrasInput);
  };

  const handleQuitarDetalle = (index) => {
    setDetallesTemporal(detallesTemporal.filter((_, i) => i !== index));
  };

  const handleActualizarCantidadDetalle = (index, nuevaCantidad) => {
    const cantidad = Number(nuevaCantidad) || 1;
    if (cantidad <= 0) return;

    const detalleActual = detallesTemporal[index];
    if (!detalleActual) return;

    const articulo = articulos.find((a) => a.art_id === detalleActual.articulo_id);
    if (articulo && articulo.art_mueve_stock && articulo.art_stock_actual < cantidad) {
      message.error(`Stock insuficiente para ${articulo.art_nombre}. Stock disponible: ${articulo.art_stock_actual}`);
      return;
    }

    setDetallesTemporal((prev) =>
      prev.map((d, i) =>
        i === index
          ? {
              ...d,
              cantidad,
              subtotal: cantidad * (Number(d.precio_unitario) || 0),
            }
          : d
      )
    );
  };

  const buildVentaPayload = (values) => {
    const esCuentaCorriente = tipoDestino === 'mostrador' && values.metodo_pago && values.metodo_pago.toLowerCase() === 'cuenta corriente';

    return {
      fecha: values.fecha.format('YYYY-MM-DD'),
      metodo_pago: tipoDestino === 'mostrador' ? values.metodo_pago : null,
      observaciones: values.observaciones || null,
      tipo_destino: tipoDestino,
      cancha_club_id: tipoDestino === 'cancha' ? canchaClubId : null,
      turno_club_id: tipoDestino === 'cancha' ? turnoClubId : null,
      mesa_id: tipoDestino === 'mesa' ? mesaId : null,
      responsable_mesa: tipoDestino === 'mesa' ? (responsableMesa ? responsableMesa.trim() : null) : null,
      jugador_club_id: esCuentaCorriente ? jugadorSeleccionado : null,
      descuento_porcentaje: tipoDestino === 'mostrador' && (porcentajeDescuento || 0) > 0 ? porcentajeDescuento : null,
      detalles: detallesTemporal.map(d => ({
        articulo_id: d.articulo_id,
        cantidad: d.cantidad,
        precio_unitario: d.precio_unitario,
      })),
    };
  };

  const buildPrintPayload = (values) => {
    const subtotal = detallesTemporal.reduce((sum, d) => sum + (d.subtotal || 0), 0);
    const pct = tipoDestino === 'mostrador' ? (porcentajeDescuento || 0) : 0;
    const totalFinal = pct > 0 ? Math.round(subtotal * (1 - pct / 100) * 100) / 100 : subtotal;
    let destinoDetalle = '';

    if (tipoDestino === 'mesa') {
      const mesa = mesasLibres.find((m) => m.mes_id === mesaId) || mesas.find((m) => m.mes_id === mesaId);
      destinoDetalle = mesa?.mes_nombre ? `Mesa: ${mesa.mes_nombre}` : (mesaId ? `Mesa ID: ${mesaId}` : '');
    }

    if (tipoDestino === 'cancha') {
      const turno = turnosClub.find((t) => t.tcl_id === turnoClubId);
      if (turno) {
        const canchaNombre = canchas.find((c) => c.ccl_id === turno.tcl_cancha_club)?.ccl_nombre ?? `Cancha ${turno.tcl_cancha_club}`;
        const hd = turno.tcl_hora_desde ? String(turno.tcl_hora_desde).substring(0, 5) : '';
        const hh = turno.tcl_hora_hasta ? String(turno.tcl_hora_hasta).substring(0, 5) : '';
        const franja = hd && hh ? ` (${hd} - ${hh})` : '';
        destinoDetalle = `Cancha: ${canchaNombre}${franja}`;
      } else if (canchaClubId) {
        const canchaNombre = canchas.find((c) => c.ccl_id === canchaClubId)?.ccl_nombre;
        destinoDetalle = canchaNombre ? `Cancha: ${canchaNombre}` : `Cancha ID: ${canchaClubId}`;
      }
    }

    return {
      tipoVenta: tipoDestino,
      destinoDetalle,
      fechaHora: dayjs().format('DD/MM/YYYY HH:mm'),
      items: detallesTemporal.map(d => ({
        descripcion: d.articulo_nombre,
        cantidad: Number(d.cantidad) || 0,
        precio: Number(d.precio_unitario) || 0,
      })),
      total: totalFinal,
    };
  };

  const handleSave = async ({ printBeforeSave = false } = {}) => {
    if (saving) return;
    try {
      setSaving(true);
      const values = await form.validateFields();

      if (detallesTemporal.length === 0) {
        message.error('Debe agregar al menos un artículo');
        return;
      }

      // Validar stock antes de enviar
      for (const detalle of detallesTemporal) {
        const articulo = articulos.find(a => a.art_id === detalle.articulo_id);
        if (articulo && articulo.art_mueve_stock && articulo.art_stock_actual < detalle.cantidad) {
          message.error(`Stock insuficiente para ${articulo.art_nombre}. Stock disponible: ${articulo.art_stock_actual}`);
          return;
        }
      }
      if (tipoDestino === 'cancha' && !turnoClubId) {
        message.error('Seleccione el turno');
        return;
      }
      if (tipoDestino === 'mesa' && !mesaId) {
        message.error('Seleccione la mesa');
        return;
      }
      
      // Validar cuenta corriente
      const esCuentaCorriente = tipoDestino === 'mostrador' && values.metodo_pago && values.metodo_pago.toLowerCase() === 'cuenta corriente';
      if (esCuentaCorriente && !jugadorSeleccionado) {
        message.error('Debe seleccionar un jugador para ventas en cuenta corriente');
        return;
      }

      if (printBeforeSave) {
        const printResponse = await apiClient.post('/gestion/ventas/print-sale', buildPrintPayload(values));
        const printPayload = await printResponse.json().catch(() => ({}));
        if (!printResponse.ok) {
          message.error(printPayload.message || printPayload.error || 'No se pudo imprimir el ticket');
          return;
        }
        if (printPayload?.pdf_base64 && !openPdfBase64AndPrint(printPayload.pdf_base64)) {
          message.warning('Permita ventanas emergentes para abrir e imprimir el ticket.');
          return;
        }
      }

      const dataToSend = buildVentaPayload(values);

      const response = await apiClient.post('/gestion/ventas', dataToSend);
      if (response.ok) {
        message.success(printBeforeSave ? 'Ticket impreso y venta registrada correctamente' : 'Venta registrada correctamente');
        setModalVisible(false);
        setDetallesTemporal([]);
        setArticuloSeleccionado(null);
        setCantidadTemporal(1);
        setJugadorSeleccionado(null);
        loadVentas();
        loadArticulos(); // Recargar artículos para actualizar stock
      } else {
        let errMsg = 'Error al registrar la venta';
        try {
          const errBody = await response.json();
          errMsg = errBody.message || errBody.error || errMsg;
        } catch (_) {}
        message.error(errMsg);
      }
    } catch (error) {
      if (error.errorFields) return;
      console.error('Error:', error);
      message.error(error.message || 'Error al guardar la venta');
    } finally {
      setSaving(false);
    }
  };

  const handlePrintAndSave = async () => {
    await handleSave({ printBeforeSave: true });
  };

  const handleDelete = async (id) => {
    try {
      const response = await apiClient.delete(`/gestion/ventas/${id}`);
      if (response.ok) {
        message.success('Venta eliminada correctamente');
        loadVentas();
        loadArticulos();
      } else {
        const error = await response.json();
        message.error(error.error || error.message || 'Error al eliminar la venta');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al eliminar la venta');
    }
  };

  const handleVerDetalle = (venta) => {
    setVentaSeleccionada(venta);
    setModalDetalleVisible(true);
  };

  const handleReprintTicket = async (ventaId) => {
    if (reprintingId) return;
    setReprintingId(ventaId);
    try {
      const response = await apiClient.post(`/gestion/ventas/${ventaId}/reprint-ticket`, {});
      if (response.ok) {
        const payload = await response.json().catch(() => ({}));
        if (payload?.pdf_base64) {
          if (!openPdfBase64AndPrint(payload.pdf_base64)) {
            message.warning('Permita ventanas emergentes para abrir e imprimir el ticket.');
            return;
          }
          message.success(`Ticket #${ventaId} listo para imprimir en este equipo`);
        } else {
          message.success(`Ticket de venta #${ventaId} enviado a la impresora`);
        }
      } else {
        let errMsg = 'No se pudo reimprimir el ticket';
        try {
          const err = await response.json();
          errMsg = err.message || err.error || errMsg;
        } catch (_) {}
        message.error(errMsg);
      }
    } catch (error) {
      message.error(error.message || 'Error al reimprimir ticket');
    } finally {
      setReprintingId(null);
    }
  };

  const columns = [
    {
      title: 'N° Venta',
      dataIndex: 'ven_id',
      key: 'ven_id',
      width: 100,
      render: (id) => `#${id}`,
    },
    {
      title: 'Fecha',
      dataIndex: 'ven_fecha_creacion',
      key: 'ven_fecha_creacion',
      render: (fecha) => fecha ? parseTimestamp(fecha).format('DD/MM/YYYY HH:mm') : '-',
    },
    {
      title: 'Total',
      dataIndex: 'ven_total',
      key: 'ven_total',
      render: (total) => `$${parseFloat(total).toFixed(2)}`,
    },
    {
      title: 'Método de Pago',
      dataIndex: 'ven_metodo_pago',
      key: 'ven_metodo_pago',
      render: (metodo, record) => {
        const m = (metodo || '').toLowerCase();
        const esCuentaCorriente = m === 'cuenta corriente';
        const color = esCuentaCorriente ? 'purple' : m === 'efectivo' ? 'green' : m === 'transferencia' ? 'blue' : m === 'qr' ? 'cyan' : 'orange';
        const texto = esCuentaCorriente && record.jugador_nombre && record.jugador_apellido
          ? `${metodo} - ${record.jugador_nombre} ${record.jugador_apellido}`
          : metodo;
        return (
        <Tag color={color}>
          {texto}
        </Tag>
        );
      },
    },
    {
      title: 'Destino',
      key: 'ven_destino',
      render: (_, record) => {
        const t = record.ven_tipo_destino || 'mostrador';
        if (t === 'cancha') return <Tag color="blue">Cancha: {record.cancha_nombre || '-'}</Tag>;
        if (t === 'mesa') return <Tag color="purple">Mesa: {record.mesa_nombre || '-'}</Tag>;
        return <Tag>Mostrador</Tag>;
      },
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 200,
      render: (_, record) => (
        <Space>
          {hasPermission('ventas', 'ver_venta') && (
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleVerDetalle(record)}
            >
              Ver
            </Button>
          )}
          {hasPermission('ventas', 'ver_venta') && (
            <Button
              type="link"
              icon={<PrinterOutlined />}
              onClick={() => handleReprintTicket(record.ven_id)}
              loading={reprintingId === record.ven_id}
              disabled={reprintingId != null && reprintingId !== record.ven_id}
              title="Reimprimir ticket por la impresora"
            >
              Reimprimir
            </Button>
          )}
          {hasPermission('ventas', 'eliminar') && !record.ven_bloqueado_eliminar && (
            <Popconfirm
              title="¿Estás seguro de eliminar esta venta?"
              onConfirm={() => handleDelete(record.ven_id)}
              okText="Sí"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />}>
                Eliminar
              </Button>
            </Popconfirm>
          )}
          {hasPermission('ventas', 'eliminar') && record.ven_bloqueado_eliminar && (
            <span
              style={{ color: '#888', fontSize: 12 }}
              title="No se puede eliminar: cobros desde Cobros, pagos en cuenta corriente, pagos en caja (no solo el ingreso automático de mostrador) o rendición de caja."
            >
              No eliminable
            </span>
          )}
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16 }}>
          <h1 style={{ margin: 0, marginBottom: 16, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>
            Gestión de Ventas
          </h1>
          <Space wrap style={{ marginBottom: 16 }}>
            <Button
              icon={<ReloadOutlined />}
              onClick={loadVentas}
              loading={loading}
            >
              {isMobile ? null : 'Actualizar'}
            </Button>
            {hasPermission('ventas', 'crear') && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleOpenModal}
              >
                Nueva Venta
              </Button>
            )}
          </Space>
          <div style={{
            padding: 12,
            backgroundColor: '#2d2d2d',
            borderRadius: 8,
            marginBottom: 16,
            borderLeft: '4px solid #52c41a'
          }}>
            <p style={{ margin: 0, color: '#b0b0b0', fontSize: '13px' }}>
              <strong style={{ color: '#ffffff' }}>Nota:</strong> Solo se muestran artículos que <strong>mueven stock</strong> 
              (productos físicos). Para artículos sin stock, use el módulo de Gastos.
            </p>
          </div>

          {/* Filtros */}
          <div style={{
            marginBottom: 16,
            padding: 16,
            backgroundColor: '#2d2d2d',
            borderRadius: 8,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 12,
            alignItems: isMobile ? 'stretch' : 'flex-end'
          }}>
            <div style={{ flex: 1, minWidth: isMobile ? '100%' : '300px' }}>
              <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                Rango de fechas
              </label>
              <RangePicker
                value={fechaRango}
                onChange={(dates) => setFechaRango(dates)}
                format="DD/MM/YYYY"
                style={{ width: '100%' }}
              />
            </div>
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={ventas}
          loading={loading}
          rowKey="ven_id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total: ${total} ventas`,
          }}
        />

        {/* Modal para crear venta */}
        <Modal
          title="Nueva Venta"
          open={modalVisible}
          onCancel={() => {
            setModalVisible(false);
            form.resetFields();
            setDetallesTemporal([]);
            setArticuloSeleccionado(null);
            setCantidadTemporal(1);
            setTipoDestino('mostrador');
            setCanchaClubId(null);
            setTurnoClubId(null);
            setTurnosClub([]);
            setMesaId(null);
            setJugadorSeleccionado(null);
            setResponsableMesa('');
          }}
          footer={[
            <Button
              key="cancelar"
              onClick={() => {
                setModalVisible(false);
                form.resetFields();
                setDetallesTemporal([]);
                setArticuloSeleccionado(null);
                setCantidadTemporal(1);
                setTipoDestino('mostrador');
                setCanchaClubId(null);
                setTurnoClubId(null);
                setTurnosClub([]);
                setMesaId(null);
                setJugadorSeleccionado(null);
                setResponsableMesa('');
              }}
              disabled={saving}
            >
              Cancelar
            </Button>,
            <Button
              key="print-register"
              onClick={handlePrintAndSave}
              loading={saving}
              type="default"
              disabled={saving}
            >
              Imprimir y Registrar
            </Button>,
            <Button key="register" type="primary" onClick={() => handleSave()} loading={saving}>
              Registrar
            </Button>,
          ]}
          confirmLoading={saving}
          width={800}
        >
          <Form form={form} layout="vertical">
            {/* Fecha: se guarda (día actual al abrir el modal), no se muestra en el ABM */}
            <Form.Item
              name="fecha"
              hidden
              rules={[{ required: true, message: 'Seleccione la fecha' }]}
            >
              <DatePicker format="DD/MM/YYYY" />
            </Form.Item>

            <div style={{ display: 'flex', gap: 24, marginBottom: 16, alignItems: 'flex-start' }}>
              {/* Izquierda: destino de la venta (debajo del título del modal) */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', color: '#ffffff', marginBottom: 8 }}>Asignar venta a</div>
                <Radio.Group
                  value={tipoDestino}
                  onChange={(e) => {
                    const nuevoTipo = e.target.value;
                    setTipoDestino(nuevoTipo);
                    setCanchaClubId(null);
                    setTurnoClubId(null);
                    setTurnosClub([]);
                    setMesaId(null);
                    setResponsableMesa('');
                    if (nuevoTipo === 'cancha') {
                      const f = form.getFieldValue('fecha');
                      loadTurnosReservadosDelDia(f);
                    }
                    if (nuevoTipo !== 'mesa') {
                      setMesasLibres([]);
                    }
                    form.setFieldsValue({ metodo_pago: undefined });
                  }}
                  style={{ display: 'flex', flexDirection: 'column', gap: 6 }}
                >
                  <Radio value="mostrador">Mostrador (cliente al paso)</Radio>
                  <Radio value="cancha">Turno</Radio>
                  <Radio value="mesa">Mesa</Radio>
                </Radio.Group>
                {tipoDestino === 'cancha' && (
                  <Select
                    placeholder="Seleccione el turno reservado del día"
                    value={turnoClubId}
                    onChange={(id) => {
                      setTurnoClubId(id);
                      const t = turnosClub.find((x) => x.tcl_id === id);
                      if (t) setCanchaClubId(t.tcl_cancha_club);
                    }}
                    showSearch
                    style={{ width: '100%', marginTop: 8 }}
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes((input || '').toLowerCase())
                    }
                    options={turnosClub.map((t) => {
                      const canchaNombre = canchas.find((c) => c.ccl_id === t.tcl_cancha_club)?.ccl_nombre ?? `Cancha ${t.tcl_cancha_club}`;
                      const hd = t.tcl_hora_desde ? String(t.tcl_hora_desde).substring(0, 5) : '';
                      const hh = t.tcl_hora_hasta ? String(t.tcl_hora_hasta).substring(0, 5) : '';
                      const horario = hd && hh ? ` ${hd} - ${hh}` : '';
                      const cliente = t.rcl_nombre_cliente ? ` - ${t.rcl_nombre_cliente}` : '';
                      const label = `${canchaNombre}${horario}${cliente}`.trim() || `Turno #${t.tcl_id}`;
                      return { label, value: t.tcl_id };
                    })}
                  />
                )}
                {tipoDestino === 'mesa' && (
                  <>
                    <Select
                      placeholder="Seleccione la mesa"
                      value={mesaId}
                      onChange={(value) => {
                        setMesaId(value);
                        const mesaSeleccionada = mesasLibres.find((m) => m.mes_id === value);
                        setResponsableMesa(mesaSeleccionada?.mes_responsable || '');
                      }}
                      showSearch
                      style={{ width: '100%', marginTop: 8 }}
                      filterOption={(input, option) =>
                        (option?.children || option?.label || '').toLowerCase().includes((input || '').toLowerCase())
                      }
                      notFoundContent="No hay mesas activas"
                    >
                      {mesasLibres.map(m => (
                        <Select.Option key={m.mes_id} value={m.mes_id}>{m.mes_nombre}</Select.Option>
                      ))}
                    </Select>
                    <Form.Item label="Responsable de la mesa" style={{ marginTop: 12, marginBottom: 0 }}>
                      <Input
                        placeholder="Nombre del responsable"
                        value={responsableMesa}
                        onChange={(e) => setResponsableMesa(e.target.value)}
                      />
                    </Form.Item>
                  </>
                )}

                <Form.Item name="observaciones" label="Observaciones" style={{ marginBottom: 0, marginTop: 16 }}>
                  <TextArea rows={5} placeholder="Observaciones (opcional)" />
                </Form.Item>
              </div>

              {/* Derecha: importe + método de pago */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16, alignSelf: 'flex-start', minWidth: 0 }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: 92,
                  padding: '8px 20px',
                  backgroundColor: '#1a1a1a',
                  borderRadius: 8,
                  borderLeft: '4px solid #52c41a',
                }}>
                  <div style={{ textAlign: 'center', width: '100%' }}>
                    {(() => {
                      const subtotal = detallesTemporal.reduce((sum, d) => sum + (d.subtotal || 0), 0);
                      const pct = tipoDestino === 'mostrador' ? (porcentajeDescuento || 0) : 0;
                      const totalACobrar = pct > 0 ? Math.round(subtotal * (1 - pct / 100) * 100) / 100 : subtotal;
                      return (
                        <>
                          <div style={{ fontSize: '11px', color: '#b0b0b0', marginBottom: 2 }}>
                            {pct > 0 ? 'Total a cobrar' : 'Importe de venta'}
                          </div>
                          <div style={{
                            fontSize: '44px',
                            fontWeight: 700,
                            color: '#52c41a',
                            lineHeight: 1.1,
                          }}>
                            $ {totalACobrar.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          {pct > 0 && (
                            <div style={{ fontSize: '12px', color: '#b0b0b0', marginTop: 4 }}>
                              Subtotal $ {subtotal.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} − {pct}% dto.
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                {tipoDestino === 'mostrador' && (
                  <Form.Item
                    name="metodo_pago"
                    label="Método de Pago"
                    rules={[{ required: true, message: 'Debe elegir un método de pago antes de registrar' }]}
                  >
                    <Select 
                      placeholder="Seleccione método de pago"
                      allowClear
                      onChange={(value) => {
                        if (value && value.toLowerCase() !== 'cuenta corriente') {
                          setJugadorSeleccionado(null);
                        }
                      }}
                    >
                      {formasPago.map(fp => (
                        <Select.Option key={fp.fop_id} value={fp.fop_nombre}>
                          {fp.fop_nombre}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}
                
                {tipoDestino === 'mostrador' && metodoPago && metodoPago.toLowerCase() === 'cuenta corriente' && (
                  <Form.Item
                    label="Jugador"
                    required
                    rules={[{ required: true, message: 'Seleccione un jugador' }]}
                  >
                    <Select
                      placeholder="Seleccione un jugador"
                      value={jugadorSeleccionado}
                      onChange={(value) => setJugadorSeleccionado(value)}
                      showSearch
                      filterOption={(input, option) => {
                        const text = String(option?.children || option?.label || '').toLowerCase();
                        return text.includes((input || '').toLowerCase());
                      }}
                    >
                      {jugadores.map(jugador => (
                        <Select.Option key={jugador.jcl_id} value={jugador.jcl_id}>
                          {jugador.jcl_nombre} {jugador.jcl_apellido}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                )}

                {tipoDestino === 'mostrador' && metodoPago && (
                  <Form.Item label="Descuento (%)" help="Opcional. Aplica a cualquier método de pago.">
                    <InputNumber
                      min={0}
                      max={100}
                      step={1}
                      value={porcentajeDescuento}
                      onChange={(v) => setPorcentajeDescuento(v != null ? Number(v) : 0)}
                      style={{ width: '100%' }}
                      addonAfter="%"
                      placeholder="0"
                    />
                  </Form.Item>
                )}
              </div>
            </div>

            <Divider>Detalles de la Venta</Divider>

            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                Código de barras (escáner)
              </label>
              <Input
                ref={codigoBarrasRef}
                value={codigoBarrasInput}
                onChange={(e) => setCodigoBarrasInput(e.target.value)}
                onKeyDown={handleCodigoBarrasKeyDown}
                placeholder="Escanee el producto aquí (Enter agrega automáticamente)"
                allowClear
                autoComplete="off"
              />
              <span style={{ display: 'block', marginTop: 4, color: '#8c8c8c', fontSize: 12 }}>
                Cada escaneo agrega 1 unidad. Si el producto ya está en la lista, suma cantidad.
              </span>
            </div>

            {/* Fila: Artículo, Cantidad, Precio (desde artículo), Agregar */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'flex-end' }}>
              <div style={{ flex: 2 }}>
                <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                  Artículo
                </label>
                <Select
                  ref={articuloSelectRef}
                  placeholder="Seleccione un artículo"
                  value={articuloSeleccionado}
                  onChange={(value) => setArticuloSeleccionado(value)}
                  showSearch
                  style={{ width: '100%' }}
                  onDropdownVisibleChange={(open) => {
                    selectArticuloAbiertoRef.current = open;
                    if (open) {
                      codigoBarrasRef.current?.blur();
                    }
                  }}
                  onFocus={() => {
                    codigoBarrasRef.current?.blur();
                  }}
                  filterOption={(input, option) => {
                    const art = articulos.find((a) => a.art_id === option?.value);
                    const term = (input || '').toLowerCase();
                    const text = String(option?.label || option?.children || '').toLowerCase();
                    const codigo = art?.art_codigo_barras ? String(art.art_codigo_barras).toLowerCase() : '';
                    return text.includes(term) || codigo.includes(term);
                  }}
                >
                  {articulos.map((art) => {
                    const enLista = detallesTemporal.some((d) => d.articulo_id === art.art_id);
                    const label = `${art.art_nombre} - Stock: ${art.art_stock_actual}${enLista ? ' (en lista)' : ''}`;
                    return (
                      <Select.Option key={art.art_id} value={art.art_id} label={label}>
                        {label}
                      </Select.Option>
                    );
                  })}
                </Select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                  Cantidad
                </label>
                <InputNumber
                  min={1}
                  value={cantidadTemporal}
                  onChange={(value) => setCantidadTemporal(value || 1)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAgregarDetalle();
                    }
                  }}
                  style={{ width: '100%' }}
                  placeholder="Cant."
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: '14px' }}>
                  Precio
                </label>
                <div style={{
                  padding: '4px 11px',
                  minHeight: 32,
                  backgroundColor: '#1a1a1a',
                  borderRadius: 6,
                  border: '1px solid #434343',
                  color: '#d9d9d9',
                  display: 'flex',
                  alignItems: 'center',
                }}>
                  $ {articuloSeleccionado
                    ? (articulos.find(a => a.art_id === articuloSeleccionado)?.art_precio_venta ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                    : '0,00'}
                </div>
              </div>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAgregarDetalle}
                style={{ height: '32px' }}
              >
                Agregar
              </Button>
            </div>

            {/* Grilla de artículos agregados */}
            {detallesTemporal.length > 0 && (
              <Table
                dataSource={detallesTemporal}
                rowKey="articulo_id"
                pagination={false}
                size="small"
                columns={[
                  {
                    title: 'Artículo',
                    dataIndex: 'articulo_nombre',
                    key: 'articulo_nombre',
                  },
                  {
                    title: 'Cantidad',
                    dataIndex: 'cantidad',
                    key: 'cantidad',
                    width: 90,
                    align: 'center',
                    render: (cantidad, _, index) => (
                      <InputNumber
                        min={1}
                        value={cantidad}
                        onChange={(value) => handleActualizarCantidadDetalle(index, value)}
                        style={{ width: '100%' }}
                      />
                    ),
                  },
                  {
                    title: 'Precio unit.',
                    dataIndex: 'precio_unitario',
                    key: 'precio_unitario',
                    width: 110,
                    align: 'right',
                    render: (p) => `$${parseFloat(p).toFixed(2)}`,
                  },
                  {
                    title: 'Subtotal',
                    dataIndex: 'subtotal',
                    key: 'subtotal',
                    width: 110,
                    align: 'right',
                    render: (s) => `$${parseFloat(s).toFixed(2)}`,
                  },
                  {
                    title: 'Acción',
                    key: 'accion',
                    width: 80,
                    align: 'center',
                    render: (_, record, index) => (
                      <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleQuitarDetalle(index)}
                        size="small"
                      >
                        Quitar
                      </Button>
                    ),
                  },
                ]}
              />
            )}
          </Form>
        </Modal>

        {/* Modal para ver detalle */}
        <Modal
          title="Detalle de Venta"
          open={modalDetalleVisible}
          onCancel={() => setModalDetalleVisible(false)}
          footer={null}
          width={600}
        >
          {ventaSeleccionada && (
            <>
              <Descriptions column={1} bordered>
                <Descriptions.Item label="Fecha">
                  {ventaSeleccionada.ven_fecha_creacion ? parseTimestamp(ventaSeleccionada.ven_fecha_creacion).format('DD/MM/YYYY HH:mm') : dayjs(ventaSeleccionada.ven_fecha).format('DD/MM/YYYY')}
                </Descriptions.Item>
                <Descriptions.Item label="Método de Pago">
                  <Tag color={(ventaSeleccionada.ven_metodo_pago || '').toLowerCase() === 'efectivo' ? 'green' : (ventaSeleccionada.ven_metodo_pago || '').toLowerCase() === 'qr' ? 'cyan' : 'blue'}>
                    {ventaSeleccionada.ven_metodo_pago}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Total">
                  ${parseFloat(ventaSeleccionada.ven_total).toFixed(2)}
                  {ventaSeleccionada.ven_descuento_porcentaje != null && Number(ventaSeleccionada.ven_descuento_porcentaje) > 0 && (
                    <span style={{ color: '#888', marginLeft: 8 }}>(con {Number(ventaSeleccionada.ven_descuento_porcentaje)}% dto.)</span>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Destino">
                  {(ventaSeleccionada.ven_tipo_destino || 'mostrador') === 'cancha' && (
                    <Tag color="blue">Cancha: {ventaSeleccionada.cancha_nombre || '-'}</Tag>
                  )}
                  {(ventaSeleccionada.ven_tipo_destino || 'mostrador') === 'mesa' && (
                    <Tag color="purple">Mesa: {ventaSeleccionada.mesa_nombre || '-'}</Tag>
                  )}
                  {(ventaSeleccionada.ven_tipo_destino || 'mostrador') === 'mostrador' && (
                    <Tag>Mostrador</Tag>
                  )}
                </Descriptions.Item>
                {ventaSeleccionada.ven_observaciones && (
                  <Descriptions.Item label="Observaciones">
                    {ventaSeleccionada.ven_observaciones}
                  </Descriptions.Item>
                )}
              </Descriptions>
              <Divider>Artículos</Divider>
              <Table
                columns={[
                  { title: 'Artículo', dataIndex: 'articulo_nombre', key: 'articulo_nombre' },
                  { title: 'Cantidad', dataIndex: 'ved_cantidad', key: 'ved_cantidad' },
                  {
                    title: 'Precio Unit.',
                    dataIndex: 'ved_precio_unitario',
                    key: 'ved_precio_unitario',
                    render: (precio) => `$${parseFloat(precio).toFixed(2)}`,
                  },
                  {
                    title: 'Subtotal',
                    dataIndex: 'ved_subtotal',
                    key: 'ved_subtotal',
                    render: (subtotal) => `$${parseFloat(subtotal).toFixed(2)}`,
                  },
                ]}
                dataSource={ventaSeleccionada.detalles || []}
                pagination={false}
                size="small"
              />
            </>
          )}
        </Modal>
      </div>
    </MainLayout>
  );
};

export default Ventas;
